import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Typography,
  MenuItem,
  ButtonGroup,
  makeStyles,
  Divider,
  IconButton,
  ButtonBase,
  Dialog
} from '@material-ui/core'
import ToggleButton from '../../ui/components/ToggleButton'
import SelectPanel from '../../ui/components/SelectPanel'
import ZoomSlider from '../../ui/components/ZoomSlider'
import SidePanel from '../../ui/components/SidePanel'
import DescriptionList from '../../ui/components/DescriptionList'
import clsx from 'clsx'
import MenuIcon from '@material-ui/icons/Menu'
import CloseIcon from '@material-ui/icons/Close'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    pointerEvents: 'none',
    top: 0,
    right: 0,
    zIndex: 10,
    padding: theme.spacing(3),
    transition: `width 
      ${theme.transitions.duration.short}ms 
      ${theme.transitions.easing.easeInOut}`,
    height: '100%',
    width: `calc(100% - ${theme.app.panelWidth}px)`,
    '& > *': {
      pointerEvents: 'all'
    },
    [theme.breakpoints.down('sm')]: {
      width: '100vw'
    }
  },
  buttonGroup: {
    position: 'absolute',
    top: 24,
    right: 24,
    '& .MuiButton-outlined': {
      borderBottomColor: theme.palette.primary.light
    }
  },
  note: {
    position: `absolute`,
    bottom: 24,
    left: 24,
    padding: theme.spacing(2),
    maxWidth: 460,
    pointerEvents: 'none',
    opacity: (props) => (props.complete ? 1 : 0),
    borderBottomColor: theme.palette.primary.light,
    transition: `transform 
      ${theme.transitions.duration.short}ms 
      ${theme.transitions.easing.easeInOut}, 
      opacity 
      ${theme.transitions.duration.short}ms 
      ${theme.transitions.easing.easeInOut}`,
    transform: (props) =>
      props.complete ? `translateY(0)` : `translateY(200px)`
  },
  italic: {
    fontStyle: 'italic'
  },
  navbar: {
    display: 'flex',
    alignItems: 'flex-start',
    '& a': {
      marginRight: 16
    },
    '& img': {
      width: 40
    }
  },
  divider: {
    marginLeft: -16,
    marginRight: -16,
    marginBottom: 40,
    display: 'block'
  },
  togglePanelButton: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginTop: -4
    }
  }
}))

const Interface = ({
  HelpComponent = <></>,
  items,
  itemTitle = 'Items',
  activeItem,
  autoRotate,
  zoom,
  showInfo,
  complete,
  onReset,
  onShowInfo,
  onSetActiveItem,
  onZoom,
  onSetAutoRotate
}) => {
  const [imageOpen, setImageOpen] = useState(false)

  // min / max zoom for the current model
  // const zoomBounds = active.zoomBounds || [0.5, 1.5]
  const zoomBounds =
    activeItem && activeItem.zoomBounds
      ? activeItem.zoomBounds
      : [0.02, 0.2]

  // amount to step on each increment / decrement of zoom slider
  const zoomStep = (zoomBounds[1] - zoomBounds[0]) / 10

  // object with class names for the interface
  const classes = useStyles({ showInfo, complete })

  const hasAutoRotate = Boolean(onSetAutoRotate)
  const hasReset = Boolean(onReset)
  const hasZoom = Boolean(onZoom)

  return activeItem ? (
    <div className={clsx('interface', classes.root)}>
      <SidePanel open={showInfo}>
        <div className="panel-top">
          <nav className={clsx('navbar', classes.navbar)}>
            <ButtonBase
              style={{ marginRight: 16 }}
              onClick={() => window.history.back()}>
              <img
                alt="Go Back"
                src="./assets/img/back_icon.png"
              />
            </ButtonBase>
            <a href="https://ssl.eas.ualberta.ca/museum-display/">
              <img
                alt="Home"
                src="./assets/img/home_icon.png"
              />
            </a>
            <a href="https://ssl.eas.ualberta.ca/museum-display/acknowledgements.php">
              <img
                alt="Acknowledgements"
                src="./assets/img/team_icon.png"
              />
            </a>
            <IconButton
              className={classes.togglePanelButton}
              style={{ marginLeft: 'auto' }}
              onClick={onShowInfo}>
              <CloseIcon />
            </IconButton>
          </nav>
          <Divider className={classes.divider} />
          <SelectPanel
            title={itemTitle}
            italic={activeItem.italic}
            value={activeItem.id}
            onChange={onSetActiveItem}>
            {items.map((m) => (
              <MenuItem
                className={m.italic ? classes.italic : null}
                key={m.id}
                value={m.id}>
                {m.name}
              </MenuItem>
            ))}
          </SelectPanel>

          {activeItem.description && (
            <Typography component="div" gutterBottom>
              <DescriptionList
                items={activeItem.description}
              />
            </Typography>
          )}
          {activeItem.image && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setImageOpen(true)
              }}>
              View Image
            </Button>
          )}
        </div>
        {HelpComponent}
      </SidePanel>
      <IconButton
        className={classes.togglePanelButton}
        onClick={onShowInfo}>
        <MenuIcon />
      </IconButton>
      <ButtonGroup className={classes.buttonGroup}>
        {hasAutoRotate && (
          <ToggleButton
            on={autoRotate}
            onClick={onSetAutoRotate}>
            Auto Rotate
          </ToggleButton>
        )}
        {hasReset && (
          <Button onClick={() => onReset(activeItem)}>
            Reset
          </Button>
        )}
      </ButtonGroup>
      {hasZoom && (
        <ZoomSlider
          min={zoomBounds[0]}
          max={zoomBounds[1]}
          step={zoomStep}
          orientation="vertical"
          value={zoom}
          marks
          onZoomOut={() =>
            zoom > zoomBounds[0] && onZoom(zoom - zoomStep)
          }
          onZoomIn={() =>
            zoom < zoomBounds[1] && onZoom(zoom + zoomStep)
          }
          onChange={(e, val) => val !== zoom && onZoom(val)}
        />
      )}
      <Dialog
        open={imageOpen}
        onClose={() => {
          setImageOpen(false)
        }}>
        <IconButton
          edge="start"
          color="inherit"
          onClick={() => {
            setImageOpen(false)
          }}
          aria-label="close"
          style={{
            position: 'absolute',
            right: 16,
            top: 16
          }}>
          <CloseIcon />
        </IconButton>
        <img
          alt={activeItem.name}
          src={activeItem.image}
          style={{
            objectFit: 'contain',
            maxWidth: '100%',
            maxHeight: 'calc(100vh - 72px)'
          }}
        />
      </Dialog>
    </div>
  ) : null
}

Interface.propTypes = {
  onReset: PropTypes.func
}

export default Interface
